import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { loadCSS, loadScript } from '../../../utils/scriptLoader';

/* ── Hero city pins (amber — all USA regions) ─────────────────── */
const PINS = [
  // Northeast
  { lat: 40.7128, lng: -74.0060, city: 'New York',       state: 'NY', certs: 142, value: '$8.2M' },
  { lat: 42.3601, lng: -71.0589, city: 'Boston',         state: 'MA', certs:  67, value: '$3.1M' },
  { lat: 39.9526, lng: -75.1652, city: 'Philadelphia',   state: 'PA', certs:  89, value: '$4.4M' },
  // Southeast
  { lat: 33.7490, lng: -84.3880, city: 'Atlanta',        state: 'GA', certs:  78, value: '$3.7M' },
  { lat: 25.7617, lng: -80.1918, city: 'Miami',          state: 'FL', certs:  94, value: '$5.2M' },
  { lat: 36.1627, lng: -86.7816, city: 'Nashville',      state: 'TN', certs:  51, value: '$2.3M' },
  // Midwest
  { lat: 41.8781, lng: -87.6298, city: 'Chicago',        state: 'IL', certs: 131, value: '$7.8M' },
  { lat: 44.9778, lng: -93.2650, city: 'Minneapolis',    state: 'MN', certs:  58, value: '$2.9M' },
  { lat: 41.4993, lng: -81.6944, city: 'Cleveland',      state: 'OH', certs:  76, value: '$3.4M' },
  // Southwest
  { lat: 32.7767, lng: -96.7970, city: 'Dallas',         state: 'TX', certs: 118, value: '$6.3M' },
  { lat: 39.7392, lng: -104.990, city: 'Denver',         state: 'CO', certs:  63, value: '$2.8M' },
  { lat: 33.4484, lng: -112.074, city: 'Phoenix',        state: 'AZ', certs:  95, value: '$4.9M' },
  // West
  { lat: 34.0522, lng: -118.244, city: 'Los Angeles',    state: 'CA', certs: 158, value: '$9.4M' },
  { lat: 47.6062, lng: -122.332, city: 'Seattle',        state: 'WA', certs:  72, value: '$3.6M' },
  { lat: 37.7749, lng: -122.419, city: 'San Francisco',  state: 'CA', certs:  84, value: '$5.8M' },
];

/* ── CDN ─────────────────────────────────────────────────────── */
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const CLUSTER_CSS = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css';
const CLUSTER_JS  = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';

/* ── Map component ───────────────────────────────────────────── */
function HeroMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<unknown>(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      loadCSS(LEAFLET_CSS);
      loadCSS(CLUSTER_CSS);
      await loadScript(LEAFLET_JS);
      await loadScript(CLUSTER_JS);
      if (cancelled || !containerRef.current) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const L = (window as any).L;
      if (!L || mapRef.current) return;

      const map = L.map(containerRef.current, {
        center: [39.5, -98.35],
        zoom: 4,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: true,
      });
      mapRef.current = map;

      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        { subdomains: 'abcd', maxZoom: 19 }
      ).addTo(map);

      L.control.attribution({ prefix: false, position: 'bottomright' })
        .addAttribution('&copy; <a href="https://carto.com/" style="color:#f59e0b">CARTO</a>')
        .addTo(map);

      L.control.zoom({ position: 'topright' }).addTo(map);

      /* ── Amber cluster group ── */
      const clusterGroup = L.markerClusterGroup({
        maxClusterRadius: 55,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        iconCreateFunction: (cluster: unknown) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const count = (cluster as any).getChildCount();
          const size  = count > 8 ? 52 : count > 4 ? 44 : 36;
          return L.divIcon({
            className: '',
            iconSize:  [size, size],
            iconAnchor: [size / 2, size / 2],
            html: `<div style="
              width:${size}px;height:${size}px;border-radius:50%;
              background:radial-gradient(circle at 35% 35%,#fbbf24,#d97706);
              border:2.5px solid rgba(251,191,36,0.6);
              display:flex;align-items:center;justify-content:center;
              font-weight:700;font-size:${count > 8 ? 14 : 12}px;
              color:#0f0a00;
              box-shadow:0 0 16px rgba(245,158,11,0.5);
            ">${count}</div>`,
          });
        },
      });

      /* ── Individual amber pins ── */
      PINS.forEach((pin) => {
        const marker = L.circleMarker([pin.lat, pin.lng], {
          radius:      9,
          fillColor:   '#f59e0b',
          color:       '#ffffff',
          weight:      2,
          fillOpacity: 0.9,
        });

        marker.bindPopup(`
          <div style="
            background:#141825;border:1px solid rgba(255,255,255,0.08);
            border-radius:10px;padding:14px 16px;min-width:190px;
            font-family:'Inter',sans-serif;
          ">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
              <span style="
                background:#f59e0b22;color:#f59e0b;
                font-size:10px;font-weight:700;padding:2px 7px;border-radius:20px;
              ">${pin.state}</span>
              <span style="color:#fff;font-size:13px;font-weight:600;">${pin.city}</span>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
              <div>
                <div style="color:rgba(255,255,255,0.35);font-size:10px;text-transform:uppercase;letter-spacing:.05em;">Certificates</div>
                <div style="color:#f59e0b;font-size:16px;font-weight:700;">${pin.certs}</div>
              </div>
              <div>
                <div style="color:rgba(255,255,255,0.35);font-size:10px;text-transform:uppercase;letter-spacing:.05em;">Face Value</div>
                <div style="color:#fff;font-size:14px;font-weight:600;">${pin.value}</div>
              </div>
            </div>
            <div style="margin-top:10px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.06);">
              <span style="color:rgba(255,255,255,0.35);font-size:10px;">Tax-Delinquent Properties — Nationwide</span>
            </div>
          </div>
        `, {
          maxWidth: 240,
          className: 'hero-map-popup',
        });

        clusterGroup.addLayer(marker);
      });

      map.addLayer(clusterGroup);

      /* ── Custom popup + zoom styles ── */
      if (!document.getElementById('hero-map-popup-style')) {
        const style = document.createElement('style');
        style.id = 'hero-map-popup-style';
        style.innerHTML = `
          .hero-map-popup .leaflet-popup-content-wrapper {
            background: transparent !important;
            box-shadow: 0 8px 32px rgba(0,0,0,0.6) !important;
            padding: 0 !important;
            border-radius: 10px !important;
          }
          .hero-map-popup .leaflet-popup-content { margin: 0 !important; }
          .hero-map-popup .leaflet-popup-tip-container { display: none !important; }
          .leaflet-zoom-in, .leaflet-zoom-out {
            background: #141825 !important;
            color: #f59e0b !important;
            border-color: rgba(255,255,255,0.08) !important;
          }
          .leaflet-zoom-in:hover, .leaflet-zoom-out:hover { background: #1c2132 !important; }
          .leaflet-bar { border: 1px solid rgba(255,255,255,0.08) !important; box-shadow: none !important; }
          .leaflet-control-attribution {
            background: rgba(7,12,20,0.7) !important;
            color: rgba(255,255,255,0.3) !important;
            font-size: 9px !important;
            padding: 2px 6px !important;
          }
          .leaflet-control-attribution a { color: #f59e0b !important; }
        `;
        document.head.appendChild(style);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (mapRef.current as any).remove();
        mapRef.current = null;
      }
    };
  }, []);

  return <div ref={containerRef} className="absolute inset-0" />;
}

/* ── Hero section ────────────────────────────────────────────── */
export default function HeroSection() {
  const scrollToMap = () => {
    document.getElementById('hero-map-anchor')?.scrollIntoView({ behavior: 'smooth' });
  };

  const totalCerts = PINS.reduce((s, p) => s + p.certs, 0);

  return (
    <section className="relative bg-[#070c14] flex flex-col overflow-hidden">

      {/* ── Top content band ── */}
      <div className="relative z-20 w-full pt-32 pb-12 px-6 md:px-10">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-amber-500/6 blur-[140px] pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">
              Live Certificate Acquisition Map — All 50 States
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-[58px] font-bold text-white leading-[1.1] max-w-4xl mb-5">
            Find &amp; Acquire Tax Lien Certificates
            <br className="hidden lg:block" />
            <span className="text-amber-400"> Across the United States</span>
          </h1>

          {/* Sub-heading */}
          <p className="text-white/55 text-lg leading-relaxed max-w-2xl mb-8">
            Professional foreclosure recovery services — we research, identify, and help you acquire
            tax-delinquent properties in all 50 states before they reach public auction.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <button
              onClick={scrollToMap}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-400 text-[#070c14] font-bold rounded-xl transition-all duration-200 whitespace-nowrap text-base cursor-pointer"
            >
              <i className="ri-map-pin-2-line" />
              Explore the Map
            </button>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/15 text-white/70 font-semibold rounded-xl hover:border-amber-500/40 hover:text-white transition-all duration-200 whitespace-nowrap text-base"
            >
              View Membership Plans
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-white/5">
            {[
              { icon: 'ri-map-2-line',        label: 'Nationwide Coverage — All 50 States' },
              { icon: 'ri-shield-check-line', label: '8,400+ Certificates Acquired' },
              { icon: 'ri-percent-line',      label: 'Up to 18% Annual Returns' },
              { icon: 'ri-time-line',         label: 'Done-For-You Service' },
            ].map((t) => (
              <div key={t.label} className="flex items-center gap-2 text-white/35 text-xs">
                <i className={`${t.icon} text-amber-400 text-sm`} />
                <span>{t.label}</span>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="mt-6 flex items-start gap-3 px-4 py-3 bg-amber-500/8 border border-amber-500/20 rounded-xl">
            <i className="ri-information-line text-amber-400 text-sm flex-shrink-0 mt-0.5" />
            <p className="text-white/40 text-xs leading-relaxed">
              <span className="text-amber-400 font-semibold">Important:</span> All tax lien certificates and tax deeds are purchased directly through your county government office. Foreclosure Recovery provides research, data, and acquisition forms only — we are not a broker, dealer, or seller of tax certificates.
            </p>
          </div>
        </div>
      </div>

      {/* ── Full-width interactive map ── */}
      <div id="hero-map-anchor" className="relative w-full" style={{ height: '580px' }}>
        {/* Top fade */}
        <div className="absolute top-0 left-0 right-0 h-10 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, #070c14, transparent)' }} />

        {/* Map */}
        <div className="absolute inset-0 z-0">
          <HeroMap />
        </div>

        {/* Legend — bottom left */}
        <div className="absolute bottom-5 left-5 z-20 flex flex-col gap-2 bg-[#0d1520]/85 backdrop-blur-sm border border-white/8 rounded-xl px-4 py-3">
          <p className="text-white/35 text-[10px] uppercase tracking-widest mb-1">Map Key</p>
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 rounded-full flex-shrink-0 border border-white/30 bg-amber-400" />
            <span className="text-white/70 text-xs whitespace-nowrap">
              <span className="font-semibold text-white/90">Amber</span> — Tax-Delinquent Property
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="text-white/35 text-[10px]">Active in all 50 states</span>
          </div>
        </div>

        {/* Certificate count badge — bottom right */}
        <div className="absolute bottom-5 right-5 z-20 bg-[#0d1520]/85 backdrop-blur-sm border border-white/8 rounded-xl px-4 py-3 text-right">
          <p className="text-amber-400 text-xl font-bold leading-none">{totalCerts.toLocaleString()}</p>
          <p className="text-white/40 text-xs mt-1">Active Certificates</p>
          <p className="text-white/25 text-[10px]">across {PINS.length} major metros</p>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-12 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to top, #090b10, transparent)' }} />
      </div>

    </section>
  );
}
